import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandXXS } from '@sorare/core/src/atoms/typography';
import { TaskCampaignDialog } from '@sorare/core/src/components/dialog/TaskCampaignDialog';
import { fantasy } from '@sorare/core/src/lib/glossary';
import type { Color } from '@sorare/core/src/style/types';

import type {
  StreaksPromotionQuery,
  StreaksPromotionQueryVariables,
} from './__generated__/index.graphql.ts';
import { StreaksPromotionWrapper } from './ui.tsx';

const Image = styled.img`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
`;

type Props = {
  promotions: { imageSrc: string; color: Color; name: string }[];
};

const STREAKS_PROMOTION_QUERY = gql`
  query StreaksPromotionQuery {
    taskCampaignsHomepage {
      tutorial {
        ...TaskCampaignDialog_taskCampaignsHomepageTutorial
      }
    }
  }
  ${TaskCampaignDialog.fragments.taskCampaignsHomepageTutorial}
` as TypedDocumentNode<StreaksPromotionQuery, StreaksPromotionQueryVariables>;

export const StreaksPromotion = ({ promotions }: Props) => {
  const [dialogOpened, setDialogOpened] = useState(false);
  const [load, { data }] = useLazyQuery(STREAKS_PROMOTION_QUERY);
  if (!promotions.length) {
    return null;
  }
  return (
    <ButtonBase
      onClick={async () => {
        const res = await load();
        setDialogOpened(!!res.data?.taskCampaignsHomepage);
      }}
    >
      <StreaksPromotionWrapper>
        <Vertical>
          <BrandXXS color="var(--c-yellow-400)">
            <FormattedMessage defaultMessage="Special Boost" />
          </BrandXXS>
          <BrandM>
            <FormattedMessage {...fantasy.hotStreaks} />
          </BrandM>
        </Vertical>
        <Vertical gap={0.5}>
          {promotions.map(({ imageSrc, color, name }, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Horizontal key={i}>
              <Image src={imageSrc} alt="" />
              <BrandXXS
                className="text-gradient-color"
                style={
                  {
                    '--text-gradient-color': color,
                  } as CSSProperties
                }
              >
                {name}
              </BrandXXS>
            </Horizontal>
          ))}
        </Vertical>
        {!!data?.taskCampaignsHomepage.tutorial && (
          <TaskCampaignDialog
            open={dialogOpened}
            onClose={() => setDialogOpened(false)}
            taskCampaignsHomepageTutorial={data.taskCampaignsHomepage.tutorial}
          />
        )}
      </StreaksPromotionWrapper>
    </ButtonBase>
  );
};
